<script setup lang="ts">
    import { onMounted, ref, toRefs } from "vue";
    import moment from "moment";
    import { useStore } from "@/store";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import ButtonLink from "@/components/ButtonLink.vue";
    import KSection from "@/components/generic/KSection.vue";
    import KFormDisplay from "@/components/forms/displays/FormDisplay/KFormDisplay.vue";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import { ProfileType } from "@/types";

    const store = useStore();

    const props = defineProps<{
        operationsanmalanId: any;
        readonly: boolean;
        expanded: boolean;
    }>();

    const { operationsanmalanId, readonly, expanded } = toRefs(props);

    type ExternStralningsregistrering = {
        registrering_genomford: boolean;
        link_opened_by: string | null;
        link_opened_at: any;
        link_opened_by_display: ProfileType | null;
    };

    const externStralningsregistrering = ref<ExternStralningsregistrering>({
        registrering_genomford: false,
        link_opened_by: null,
        link_opened_at: null,
        link_opened_by_display: null,
    });

    const emit = defineEmits(["submit"]);

    onMounted(async () => {
        try {
            const response = await klinikenApi.get(
                `/operation/operationsjournaler/${operationsanmalanId.value}/externstralningsregistrering`,
                getConfig()
            );

            externStralningsregistrering.value = response.data;
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        }
    });

    const openLinkRontgenutvecklarna = async () => {
        window.open(store.state.config?.operation.OPERATION_JOURNAL_EXTERN_STRALNINGSREGISTRERING_URL);

        externStralningsregistrering.value.link_opened_at = moment();
        externStralningsregistrering.value.link_opened_by = store.getters["userData/currentProfile"].id;
        externStralningsregistrering.value.link_opened_by_display = store.getters["userData/currentProfile"];

        const payload = {
            link_opened_at: externStralningsregistrering.value.link_opened_at,
            link_opened_by: externStralningsregistrering.value.link_opened_by,
        };

        emit(
            "submit",
            payload,
            "Strålningsregistrering sparad",
            "externstralningsregistrering/",
            undefined,
            "Strålningsregistrering"
        );
    };

    const toggleRegistreringGenomford = async (e: any) => {
        const payload = {
            registrering_genomford: externStralningsregistrering.value.registrering_genomford,
        };
        emit(
            "submit",
            payload,
            "Strålningsregistrering sparad",
            "externstralningsregistrering/",
            undefined,
            "Strålningsregistrering"
        );
    };
</script>

<template>
    <KSection
        name="extern_stralningsregistrering"
        class="extern-stralningsregistrering"
        heading="Strålningsregistrering"
        :numbered="true"
        :expanded="expanded"
    >
        <div class="link-section">
            <div class="two-elements-grid">
                <ButtonLink type="button" @click.prevent="openLinkRontgenutvecklarna()" :disabled="readonly">
                    Rapportering av genomlysning
                    <small>
                        <span>(extern tjänst, öppnas i nytt fönster)</span>
                        <span class="updated" v-if="externStralningsregistrering.link_opened_at">
                            Öppnad {{ moment(externStralningsregistrering.link_opened_at).format("YYYY-MM-DD") }}
                        </span>
                    </small>
                </ButtonLink>

                <FormulateInput
                    name="registrering_genomford"
                    @change="toggleRegistreringGenomford"
                    class="bg-grey checkbox-green"
                    type="checkbox"
                    label="Registrering genomförd"
                    v-model="externStralningsregistrering.registrering_genomford"
                    :disabled="readonly"
                />
            </div>
            <KFormDisplay v-if="externStralningsregistrering.link_opened_at">
                <KFormFieldDisplay
                    name="link_opened_by"
                    label="Öppnad av"
                    :value="externStralningsregistrering.link_opened_by_display?.user_display?.name"
                />
                <KFormFieldDisplay
                    name="link_opened_at"
                    label="Datum"
                    :value="moment(externStralningsregistrering.link_opened_at).format('YYYY-MM-DD')"
                />
            </KFormDisplay>
        </div>
    </KSection>
</template>

<style lang="scss">
    .extern-stralningsregistrering {
        // Identisk med stylingen från WHO-checklista, kan göras gemensam
        .link-section {
            display: flex;
            flex-direction: column;
            gap: 20px;
            align-items: flex-start;

            .two-elements-grid {
                display: grid;
                grid-template-columns: 70% 50%;
                width: 65%;
                gap: 20px;

                .bg-grey {
                    /* padding: 1.25rem; */
                    padding-left: 2.25rem;
                    padding-top: 1.25rem;
                    width: 100%;
                    background: rgb(246, 248, 251);
                    border-radius: 4px;
                    border: 1px #8080802b solid;
                    height: 90%;
                }

                .checkbox-green {
                    ::v-deep input[type="checkbox"]:checked + label.formulate-input-element-decorator {
                        background-color: #2d9367;
                    }
                }
            }

            .button-link {
                height: 70px;
                width: 480px;
                padding: 15px inherit 15px 20px;

                display: flex;
                flex-direction: column;
                gap: 5px;
                align-items: stretch !important;
                text-align: left;

                small {
                    display: flex;
                    justify-content: space-between;

                    .updated {
                        font-style: italic;
                    }
                }
            }
        }
    }
</style>
